<script lang="ts" setup>
import InitialsAvatar from '@/components/general/InitialsAvatar.vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFileDialog } from '@vueuse/core';
import useAvatarService from '@/composables/apiServices/useAvatarService';
import { isAxiosError } from 'axios';
import useUserService from '@/composables/apiServices/useUserService';
import { useRegleConfig } from '@/composables/useRegle';
import { required, minLength, maxLength } from '@regle/rules';
import { useBeforeLeave } from '@/composables/useBeforeLeave';
import BeforeLeavingModal from '@/components/before-leaving/BeforeLeavingModal.vue';
import type { IPersonalInfoProps } from '@/types/views/settings/PersonalInfo';

const props = defineProps<IPersonalInfoProps>();
const { t } = useI18n();
const { getAvatar, uploadAvatar, deleteAvatar } = useAvatarService();
const { details, updateDetails } = useUserService();

const { data: avatarData } = getAvatar();
const { mutateAsync: addAvatar, isPending: isAddPending, isError: uploadError } = uploadAvatar();
const {
  mutateAsync: removeAvatar,
  isPending: isRemovePending,
  isError: deleteError
} = deleteAvatar();
const {
  mutateAsync: mutateUpdateUserDetails,
  error: detailsError,
  isError: nameError
} = updateDetails();
const { data: userDetails, isLoading: isUserDetailsLoading } = details();

const { open: openFile, onChange, reset } = useFileDialog({ accept: 'image/*', multiple: false });

// Initialize regle
const useCustomRegle = useRegleConfig();
const { r$ } = useCustomRegle(
  { accountName: '' },
  {
    accountName: {
      required,
      minLength: minLength(2),
      maxLength: maxLength(30)
    }
  }
);

const avatarPending = computed(() => isAddPending || isRemovePending);
const isError = computed(() => uploadError || deleteError || nameError);

const errorMessage = computed<string | undefined>(() => {
  if (detailsError.value) {
    if (isAxiosError(detailsError.value)) {
      return detailsError.value.response?.data?.message;
    }
    return detailsError.value?.message;
  }
  return r$.accountName.$errors[0];
});

onChange(async (files) => {
  if (files && files[0]) {
    const formData = new FormData();
    const imageFile = files[0];
    formData.append('avatar', imageFile);

    await addAvatar(formData);
    reset();
  }
});

const handleRemoveAvatar = async () => {
  r$.accountName.$value = '';
  await removeAvatar();
};

const handleRemoveName = async () => {
  await mutateUpdateUserDetails({ name: '' });
};

const handleSaveName = async () => {
  // Validate the form
  const isValid = await r$.accountName.$validate();
  if (!isValid) return;

  dialogOpener.value = false;
  await mutateUpdateUserDetails({ name: r$.accountName.$value });
};

const resetValues = () => {
  r$.$reset();
};

const metaDirty = computed(() => r$.$anyDirty);
const { dialogLeave, dialogPopupRef, dialogOpener, dialogClose } = useBeforeLeave(metaDirty);

// Watch for errors and revert if necessary
watch(
  () => isError.value,
  (errorOccurred) => {
    if (errorOccurred) {
      // queryClient.setQueryData(
      //   ['authenticatedQuery', userIndex.value, 'user', 'details'],
      //   (): IDetailsResponse | undefined => previousDetails.value
      // );
      reset();
    }
  }
);
</script>
<template>
  <div
    :class="[
      'personal-info d-flex flex-column',
      {
        'align-center personal-info--mobile': props.isSmallDisplay,
        'px-4': props.isSmallDisplay,
        'pt-8': props.isSmallDisplay,
        'pb-4': props.isSmallDisplay
      }
    ]"
  >
    <h2 v-if="!props.isSmallDisplay" class="text-body-lg medium-weight" sentry-unmask>
      {{ t('settings.account.personalInfo') }}
    </h2>
    <div class="personal-info__avatar">
      <InitialsAvatar
        :image="avatarData?.avatar"
        :name="userDetails?.name ?? undefined"
        class="avatar-image"
        size="100%"
      />
      <BeforeLeavingModal
        :dialog-popup="dialogPopupRef"
        form="dialog"
        @leave="dialogLeave"
      ></BeforeLeavingModal>
      <v-dialog>
        <template #activator="{ props: dialogActivatorProps }">
          <v-tooltip :text="t('common.edit')">
            <template #activator="{ props: tooltipActivatorProps }">
              <ElevatedBtn
                :aria-label="t('common.edit')"
                class="edit-btn"
                color="primary"
                data-test-id="account-avatar-edit"
                size="40"
                v-bind="{ ...dialogActivatorProps, ...tooltipActivatorProps }"
                sentry-unmask
              >
                <v-icon>mdi-pencil-outline</v-icon>
              </ElevatedBtn>
            </template>
          </v-tooltip>
        </template>
        <template #default="{ isActive }">
          <v-card class="modal" data-test-id="account-avatar-modal">
            <CloseBtn
              class="v-card__close-btn"
              color="on-surface"
              data-test-id="account-avatar-modal-close"
              icon
              @click="isActive.value = false"
              sentry-unmask
            >
              <v-icon>mdi-close</v-icon>
            </CloseBtn>
            <v-card-title sentry-unmask>
              {{ t('settings.account.editAvatar') }}
            </v-card-title>

            <InitialsAvatar
              :image="avatarData?.avatar"
              :name="userDetails?.name ?? undefined"
              class="avatar-image"
              size="110"
            />
            <v-card-actions>
              <outlined-btn
                v-if="avatarData?.avatar"
                :loading="avatarPending.value"
                data-test-id="account-avatar-delete"
                prepend-icon="mdi-trash-can-outline"
                @click="handleRemoveAvatar"
                sentry-unmask
              >
                {{ t('common.delete') }}
              </outlined-btn>
              <filled-btn
                :loading="avatarPending.value"
                :prepend-icon="avatarData?.avatar ? 'mdi-pencil-outline' : 'mdi-upload'"
                data-test-id="account-avatar-upload"
                @click="openFile()"
                sentry-unmask
              >
                {{ avatarData?.avatar ? t('common.edit') : t('common.upload') }}
              </filled-btn>
            </v-card-actions>
          </v-card>
        </template>
      </v-dialog>
    </div>
    <div
      :class="[
        'personal-info__details d-flex flex-column',
        { 'personal-info__details--mobile': props.isSmallDisplay }
      ]"
    >
      <div class="name">
        <span v-if="userDetails?.name">{{ userDetails.name }}</span>
        <v-dialog v-model="dialogOpener" :persistent="r$.$dirty" @after-leave="resetValues">
          <template v-if="!props.isSmallDisplay" #activator="{ props: activatorProps }">
            <OutlinedBtn data-test-id="account-name-edit" v-bind="activatorProps" sentry-unmask>
              {{ t('common.edit') }}
            </OutlinedBtn>
            <TextBtn
              v-if="userDetails?.name"
              class="d-block"
              data-test-id="account-name-remove"
              @click="handleRemoveName"
              sentry-unmask
            >
              {{ t('common.delete') }}
            </TextBtn>
          </template>
          <template #default="{ isActive }">
            <v-card class="modal" data-test-id="account-name-modal">
              <CloseBtn
                class="v-card__close-btn"
                color="on-surface"
                data-test-id="personal-info-close"
                @click="dialogClose"
                sentry-unmask
              >
                <v-icon>mdi-close</v-icon>
              </CloseBtn>
              <v-card-title sentry-unmask>
                {{ t('settings.account.editName') }}
              </v-card-title>

              <v-card-text>
                <div class="d-flex flex-column mb-5">
                  <span class="font-weight-medium d-block mb-2" sentry-unmask>
                    {{ t('settings.account.currentName') }}
                  </span>
                  <span>{{ userDetails?.name }}</span>
                </div>

                <form data-test-id="account-name-form" @submit.prevent="handleSaveName">
                  <div>
                    <span id="nameLabel" class="font-weight-medium d-block mb-2" sentry-unmask>
                      {{ t('settings.account.newName') }}
                    </span>
                    <v-text-field
                      v-model="r$.accountName.$value"
                      :error-messages="errorMessage"
                      aria-labelledby="nameLabel"
                      data-test-id="account-name-field"
                      variant="outlined"
                    />
                  </div>

                  <v-card-actions sentry-unmask>
                    <outlined-btn
                      data-test-id="account-name-cancel"
                      @click="isActive.value = false"
                    >
                      {{ t('common.cancel') }}
                    </outlined-btn>
                    <filled-btn data-test-id="account-name-save" @click="handleSaveName()">
                      {{ t('common.save') }}
                    </filled-btn>
                  </v-card-actions>
                </form>
              </v-card-text>
            </v-card>
          </template>
        </v-dialog>
      </div>
      <span class="address">
        {{ userDetails?.address }}
      </span>
      <template v-if="props.isSmallDisplay">
        <outlined-btn
          class="d-block"
          data-test-id="account-name-edit"
          @click="dialogOpener = true"
          sentry-unmask
        >
          {{ t('settings.account.editName') }}
        </outlined-btn>
        <text-btn
          v-if="userDetails?.name"
          class="d-block"
          data-test-id="account-name-remove"
          @click="handleRemoveName"
          sentry-unmask
        >
          {{ t('settings.account.removeName') }}
        </text-btn>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.personal-info {
  gap: 1.5rem;

  &__avatar {
    width: 7rem;
    height: 7rem;
    position: relative;

    .edit-btn {
      position: absolute;
      bottom: 0;
      right: 0;
    }
  }

  &__details {
    .name {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &--mobile {
      gap: 0.5rem;
      align-items: center;

      .address {
        color: rgb(var(--v-theme-on-surface-variant));
      }
    }
  }

  &--mobile {
    gap: 0.5rem;
  }
}

.avatar-image {
  font-size: 2rem;
}

.modal {
  display: flex;
  flex-direction: column;
  position: relative;

  .avatar-image {
    margin: 0 auto;
  }
}
</style>
